<script lang="ts" setup>
  import { ref, computed, onMounted, watch } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SkeletonLoaderInlineField from '@/components/skeleton/SkeletonLoaderInlineField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'
  import { IconTag } from '@tabler/icons-vue'
  import { useTranslation } from '@/composables/useTranslation'

  // Translation setup
  const { t } = useTranslation({
    en: {
      selectStatus: 'Select status...',
      noStatusFound: 'No status options available',
      assignText: 'Set status to',
      forTheCase: 'for the request.',
      statusAssigned: 'The status was set to',
      forTheRequest: 'for the request.',
      statusNew: 'New',
      statusInProgress: 'In Progress',
      statusCompleted: 'Completed',
      statusRejected: 'Rejected'
    },
    de: {
      selectStatus: 'Status auswählen...',
      noStatusFound: 'Keine Status-Optionen verfügbar',
      assignText: 'Setze den Status auf',
      forTheCase: 'für die Anfrage.',
      statusAssigned: 'Der Status wurde auf',
      forTheRequest: 'für die Anfrage gesetzt.',
      statusNew: 'Neu',
      statusInProgress: 'In Bearbeitung',
      statusCompleted: 'Abgeschlossen',
      statusRejected: 'Abgelehnt'
    }
  })

  // Define props
  interface Props {
    action: any // TAgentAction type from generated types
    customPayload?: any // Optional custom payload from parent
  }

  const props = defineProps<Props>()

  // Define emits
  const emit = defineEmits<{
    payloadChanged: [payload: any]
  }>()

  // Reactive state
  const selectedStatus = ref<string>('')

  // Computed properties - predefined status options
  const statusOptions = computed<Option[]>(() => {
    return [
      { value: 'NEW', title: t('statusNew') },
      { value: 'IN_PROGRESS', title: t('statusInProgress') },
      { value: 'COMPLETED', title: t('statusCompleted') },
      { value: 'REJECTED', title: t('statusRejected') }
    ]
  })

  const selectedStatusLabel = computed(() => {
    const option = statusOptions.value.find((opt) => opt.value === selectedStatus.value)
    return option?.title || selectedStatus.value
  })

  // Check if action should show summary (committed, reverted, or has error)
  const shouldShowSummary = computed(
    () =>
      props.action?.is_committed === true ||
      props.action?.is_reverted === true ||
      props.action?.has_commit_error === true ||
      (props.action?.is_commitable === false && selectedStatus.value)
  )

  // Summary text for committed/cancelled/error actions
  const summaryText = computed(() => {
    if (!shouldShowSummary.value || !selectedStatus.value) return ''

    return `${t('statusAssigned')} "${selectedStatusLabel.value}" ${t('forTheRequest')}`
  })

  // Methods
  const handleStatusSelection = (status: string) => {
    selectedStatus.value = status
    emitPayloadChange()
  }

  // Emit payload changes to parent
  const emitPayloadChange = () => {
    if (props.action?.payload?.case_uuid) {
      const payload = {
        case_uuid: props.action.payload.case_uuid,
        status: selectedStatus.value
      }
      emit('payloadChanged', payload)
    }
  }

  // Initialize selections from action payload
  const initializeFromPayload = (payload: any) => {
    if (!payload) return

    if (payload.status) {
      selectedStatus.value = payload.status
    }
  }

  // Watch for action payload changes
  watch(
    () => props.action?.payload,
    (newPayload) => {
      initializeFromPayload(newPayload)
    },
    { deep: true }
  )

  // Watch for custom payload changes
  watch(
    () => props.customPayload,
    (newCustomPayload) => {
      if (newCustomPayload) {
        initializeFromPayload(newCustomPayload)
      }
    },
    { deep: true }
  )

  // Initialize component
  onMounted(() => {
    // Set initial selection from action payload or custom payload
    initializeFromPayload(props.customPayload || props.action?.payload)

    // Emit initial payload if any values are set
    if (selectedStatus.value) {
      emitPayloadChange()
    }
  })
</script>

<template>
  <div>
    <!-- Action Summary (committed, cancelled, reverted, or error) -->
    <div
      v-if="shouldShowSummary"
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ summaryText }}</span>
      </div>
    </div>

    <!-- Action Details (when editable) -->
    <div
      v-else
      class="space-y-3"
    >
      <div class="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        <span>{{ t('assignText') }}</span>

        <!-- Status Selection -->
        <SelectField
          v-model="selectedStatus"
          variant="inline-text"
          :options="statusOptions"
          :is-filterable="false"
          :placeholder="t('selectStatus')"
          name="status"
          @update:model-value="handleStatusSelection"
        />

        <span>{{ t('forTheCase') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
